import Vue from 'vue'
import VueRouter, { Route, RouteConfig } from 'vue-router'
import VueTheMask from 'vue-the-mask'

import Home from '../views/Home.vue'

import store from '@/store'
import {
  SET_HISTORY_COUNT,
  AUTH_SET_REDIRECT,
  AUTH_NACCC_TOKEN,
  SET_QUERY,
  AUTH_SET_IAT,
  SET_TAG,
  AUTH_FALLBACK_URL,
  AUTH_HERITAGE_JOURNEY,
  AUTH_SET_FORCE_REDIRECT,
  AUTH_SET_SESSION,
  AUTH_SET_REDIRECT_LINK,
  AUTH_DISPATCH_LOGOUT,
} from '@/store/types'
import { isEmpty } from 'lodash-es'
import { fetchDomAttribute } from '@/utils/util.dom'
import { PUBLIC_PATH } from '@/config/constants'

Vue.use(VueRouter)
Vue.use(VueTheMask)

const apiKey = fetchDomAttribute('key')

const BASE_PATH = apiKey ? '' : '/lang/:lang/:merchant'

const routes: Array<RouteConfig> = [
  {
    path: BASE_PATH + '/',
    name: 'Home',
    component: Home,
  },
  {
    path: BASE_PATH + '/ticket/:id',
    name: 'ItemPage',
    component: () =>
      import(/* webpackChunkName: "ticket" */ '@/views/Ticket.vue'),
  },
  {
    path: BASE_PATH + '/checkout',
    name: 'Checkout',
    component: () =>
      import(/* webpackChunkName: "checkout" */ '@/views/Checkout.vue'),
  },
  {
    path: BASE_PATH + '/auth',
    alias: BASE_PATH + '/checkout/auth',
    name: 'Auth',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/Auth.vue'),
  },
  {
    path: BASE_PATH + '/auth/otp',
    alias: BASE_PATH + '/checkout/auth/otp',
    name: 'OTP',
    component: () => import(/* webpackChunkName: "otp" */ '@/views/OTP.vue'),
  },
  {
    path: BASE_PATH + '/my-tickets',
    name: 'MyTickets',
    component: () =>
      import(/* webpackChunkName: "my-tickets" */ '@/views/MyTickets.vue'),
  },
  {
    path: BASE_PATH + '/cart',
    name: 'Cart',
    component: () =>
      import(/* webpackChunkName: "cart" */ '@/views/Cart.vue'),
  },
  {
    path: BASE_PATH + '/my-tickets/:id',
    name: 'RedemptionGroup',
    component: () =>
      import(
        /* webpackChunkName: "redemption-group" */ '@/views/RedemptionGroup.vue'
      ),
  },
  {
    path: BASE_PATH + '/book-visit/:id',
    name: 'ItemCustomisationPage',
    component: () => import(
        /* webpackChunkName: "redemption-group" */ '@/views/BookVisit.vue'
    ),
  },
  {
    path: BASE_PATH + '/profile',
    name: 'Profile',
    component: () => import(
        /* webpackChunkName: "redemption-group" */ '@/views/Profile.vue'
    ),
  },
  {
    path: BASE_PATH + '/my-heritage',
    name: 'MyHeritagePage',
    component: () => import(
        /* webpackChunkName: "redemption-group" */ '@/views/MyHeritagePage.vue'
    ),
  },
]

const baseUrl = fetchDomAttribute('base-route')

const router = new VueRouter({
  mode: 'hash',
  routes,
  base: baseUrl ?? PUBLIC_PATH,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
})

router.onReady(() => {
  // Set history count for back button logic
  store.commit(SET_HISTORY_COUNT, window.history.length)
})

const PROTECTED_PATHS = {
  Checkout: true,
  MyTickets: true,
  RedemptionGroup: true,
  Profile: true,
  MyHeritagePage: true
}

async function redirectTo(to: Route): Promise<string | undefined> {
  const { query, path: pathFromQuery, fallbackURL, cancel, success, not_eligible} = to.query
  const {
    path: pathFromJSON = null,
    naccc_token = null,
    redirect = null,
    iat = null,
    tag = null,
    force_redirect = null,
    session = null,
    ...otherQuery
  } = typeof query === 'string' ? JSON.parse(query) as {[key: string]: string} : {}
  const path = pathFromQuery ?? pathFromJSON
  const { lang, merchant } = to.params
  const basePath = lang && merchant ? `/lang/${lang}/${merchant}` : ''
  const isAuthenticated = store.getters.isAuthenticated
  const otpSentTime = store.getters.otpSentTime
  const redirectPathAfterAuth = store.state.auth.redirectPathAfterAuth
  const sessionFromStore = store.state.auth.session

  const toPaths = to.path.split('/')
  const isToCheckout = toPaths[toPaths.length - 1].includes('checkout')

  if (naccc_token) {
    store.commit(AUTH_NACCC_TOKEN, naccc_token)
  }

  if (redirect) {
    store.commit(AUTH_SET_REDIRECT_LINK, redirect)
  }

  if (iat) {
    store.commit(AUTH_SET_IAT, { iat })
  }

  if (tag) {
    store.commit(SET_TAG, tag)
  }
  
  if (fallbackURL) {
    store.commit(AUTH_FALLBACK_URL, fallbackURL)
  }

  if (force_redirect) {
    store.commit(AUTH_SET_FORCE_REDIRECT, force_redirect)
  }

  if (session) {
    if (isAuthenticated && session !== sessionFromStore) {
      await store.dispatch(AUTH_DISPATCH_LOGOUT)
      store.commit(AUTH_SET_SESSION, session)
      return `${basePath}/auth` 
    } else {
      store.commit(AUTH_SET_SESSION, session)
    }
  }
  
  if (!isEmpty(otherQuery)) {
    store.commit(SET_QUERY, otherQuery)
  }

  if (!isAuthenticated && isToCheckout) {
    if (!(success || cancel || not_eligible)) {
      // Authenticate before checkout
      store.commit(AUTH_SET_REDIRECT, to.path)
      return `${basePath}/checkout/auth`
    }
  }

  if (to.name === 'OTP' && !otpSentTime) {
    return `${basePath}/auth`
  }

  if (!isAuthenticated && (to.name === 'Auth' || to.name === 'OTP') && !to.path.includes('checkout')) {
    if (!redirectPathAfterAuth) {
      store.commit(AUTH_SET_REDIRECT, `${basePath}/profile`)
    }
  }

  if (isAuthenticated && (to.name === 'Auth' || to.name === 'OTP')) {
    // Trying to sign in when signed in
    
    if (path === 'browse') {
      return `${basePath}/`
    } else if (path === 'tickets') {
      return `${basePath}/my-tickets`
    } else {
      return `${basePath}/${path || ''}`
    }
  }

  if (!isAuthenticated && to.name && to.name in PROTECTED_PATHS) {
    if (!(success || cancel || not_eligible)) {
      // Pages that need sign in
      store.commit(AUTH_SET_REDIRECT, `${basePath}`)
      return `${basePath}/auth`
    }
    
  }

  //Path for single module
  if (path === 'browse') {
    // Set redirect to auth page with params
    return `${basePath}/`
  }

  if (path === 'tickets') {
    if (isAuthenticated) {
      return `${basePath}/my-tickets`
    } else {
      store.commit(AUTH_SET_REDIRECT, `${basePath}/my-tickets`)
      return `${basePath}/auth`
    }
  }

  if(path === 'heritage_journey') {
    store.commit(AUTH_HERITAGE_JOURNEY, true)

    if (!isAuthenticated) {
      return `${basePath}/auth`
    } else {
      const url = new URL(fallbackURL.toString())
      url.searchParams.delete('vpath')
      url.searchParams.delete('vquery')
      window.open(url.href, '_top')
    }
  }

  if (path) {
    return `${basePath}/${path}`
  }
}

router.beforeEach(async (to, from, next) => {
  document.title = store.getters.shopName

  next({ path: await redirectTo(to) })
})

export default router
